import { useRef, useState, useEffect, useCallback } from "react";
import {
  Bold, Italic, Underline, Strikethrough, Link, Image, ListOrdered, List,
  AlignLeft, AlignCenter, AlignRight, Code, ChevronDown,
  Undo2, Redo2, Palette, Highlighter, Indent, Outdent,
  Minus, Code2, Quote, Eraser, MousePointer2, Table as TableIcon,
  Eye, HelpCircle,
} from "lucide-react";
import "./WysiwygEditor.css";
import { detectElement } from "./editor/detectElement";
import { chipifyHtml, dechipifyHtml } from "./editor/mergeTagChips";
import { LIQUID_SNIPPETS } from "./editor/liquidSnippets";
import { validateLiquid } from "./editor/liquidValidate";
import LiquidCheatsheet from "./editor/LiquidCheatsheet";
import {
  exec, formatBlock, applyFontSize, applyLineHeight, toggleInlineCode,
  insertDivider, clearFormatting, applyForeColor, applyBackColor,
} from "./editor/commands";
import ColorPopover from "./editor/ColorPopover";
import LinkBubble from "./editor/bubbles/LinkBubble";
import ImageBubble from "./editor/bubbles/ImageBubble";
import ButtonBubble from "./editor/bubbles/ButtonBubble";
import TableBubble from "./editor/bubbles/TableBubble";
import TableGridPicker from "./editor/bubbles/TableGridPicker";
import BlockBubble from "./editor/bubbles/BlockBubble";
import DividerBubble from "./editor/bubbles/DividerBubble";
import LiquidChipBubble from "./editor/bubbles/LiquidChipBubble";

const MERGE_TAGS = [
  { label: "First Name", value: "{{firstName}}" },
  { label: "Last Name", value: "{{lastName}}" },
  { label: "Email", value: "{{email}}" },
  { label: "Company", value: "{{company}}" },
  { label: "Title", value: "{{title}}" },
  { label: "Phone", value: "{{phone}}" },
];

export default function WysiwygEditor({ value = "", onChange, liquidPreview = null }) {
  const iframeRef = useRef(null);
  const [sourceMode, setSourceMode] = useState(false);
  const [sourceValue, setSourceValue] = useState(value);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [snippetsOpen, setSnippetsOpen] = useState(false);
  const [liquidError, setLiquidError] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const [cheatsheetOpen, setCheatsheetOpen] = useState(false);
  const [foreOpen, setForeOpen] = useState(false);
  const [backOpen, setBackOpen] = useState(false);
  const [tableGridOpen, setTableGridOpen] = useState(false);
  const [bubble, setBubble] = useState(null);

  const mergeRef = useRef(null);
  const snippetsRef = useRef(null);
  const foreColorRef = useRef(null);
  const backColorRef = useRef(null);
  const tableGridRef = useRef(null);
  const skipNextSync = useRef(false);
  const onChangeRef = useRef(onChange);
  const iframeReady = useRef(false);

  useEffect(() => { onChangeRef.current = onChange; }, [onChange]);

  const getDoc = useCallback(() => {
    const iframe = iframeRef.current;
    return iframe?.contentDocument || iframe?.contentWindow?.document;
  }, []);

  const fireChange = useCallback(() => {
    const doc = getDoc();
    if (!doc?.body) return;
    const html = dechipifyHtml(doc.body.innerHTML);
    skipNextSync.current = true;
    onChangeRef.current?.(html);
  }, [getDoc]);

  // Debounced liquid validation
  useEffect(() => {
    const t = setTimeout(() => {
      const html = sourceMode ? sourceValue : value;
      setLiquidError(validateLiquid(html || ""));
    }, 300);
    return () => clearTimeout(t);
  }, [value, sourceValue, sourceMode]);

  // Debounced server-side preview render
  useEffect(() => {
    if (!previewMode || !liquidPreview) return;
    const html = sourceMode ? sourceValue : value;
    let cancelled = false;
    setPreviewLoading(true);
    setPreviewError(null);
    const t = setTimeout(async () => {
      try {
        const result = await liquidPreview(html || "");
        if (!cancelled) setPreviewHtml(result || "");
      } catch (err) {
        if (!cancelled) setPreviewError(err?.message || "Preview failed");
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(t); };
  }, [previewMode, value, sourceValue, sourceMode, liquidPreview]);

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || sourceMode) return;

    iframeReady.current = false;

    const onInput = () => fireChange();
    const onPaste = () => setTimeout(() => fireChange(), 0);
    const onClick = (e) => {
      const res = detectElement(e.target);
      setBubble(res);
    };

    const initIframe = () => {
      const doc = getDoc();
      if (!doc) return;

      doc.open();
      doc.write(`<!DOCTYPE html>
<html>
<head>
<style>
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    line-height: 1.6;
    color: #333;
    background-color: #ffffff;
    padding: 12px;
    margin: 0;
    min-height: 100%;
    word-wrap: break-word;
    outline: none;
  }
  body:empty::before {
    content: 'Start writing your email...';
    color: #999;
  }
  img { max-width: 100%; height: auto; }
  a { color: #4f46e5; }
  table { border-collapse: collapse; }
  blockquote { border-left: 3px solid #e2e8f0; padding-left: 1rem; color: #64748b; margin: 0.5rem 0; }
  code { background: #f1f5f9; padding: 1px 6px; border-radius: 4px; font-family: monospace; font-size: 0.9em; }
  .merge-tag, .liquid-chip-output {
    display: inline-block;
    padding: 1px 10px;
    margin: 0 1px;
    border-radius: 10px;
    background: linear-gradient(135deg, #6366f1, #8b5cf6);
    color: #ffffff;
    font-size: 0.85em;
    font-weight: 600;
    user-select: all;
  }
  .liquid-chip-tag {
    display: inline-block;
    padding: 1px 10px;
    margin: 0 1px;
    border-radius: 10px;
    background: linear-gradient(135deg, #f59e0b, #d97706);
    color: #ffffff;
    font-size: 0.82em;
    font-weight: 600;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    user-select: all;
  }
</style>
</head>
<body contenteditable="true"></body>
</html>`);
      doc.close();

      iframeReady.current = true;

      if (value) {
        doc.body.innerHTML = chipifyHtml(value);
      }

      doc.body.addEventListener("input", onInput);
      doc.body.addEventListener("paste", onPaste);
      doc.body.addEventListener("click", onClick);
    };

    const timer = setTimeout(initIframe, 0);

    return () => {
      clearTimeout(timer);
      const doc = getDoc();
      if (doc?.body) {
        doc.body.removeEventListener("input", onInput);
        doc.body.removeEventListener("paste", onPaste);
        doc.body.removeEventListener("click", onClick);
      }
      iframeReady.current = false;
    };
  }, [sourceMode]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (skipNextSync.current) {
      skipNextSync.current = false;
      return;
    }
    if (sourceMode) {
      setSourceValue(value);
    } else if (iframeReady.current) {
      const doc = getDoc();
      if (doc?.body) {
        doc.body.innerHTML = chipifyHtml(value || "");
      }
    }
  }, [value, sourceMode, getDoc]);

  const execCommand = (cmd, val = null) => {
    const doc = getDoc();
    if (!doc) return;
    doc.body.focus();
    doc.execCommand(cmd, false, val);
    fireChange();
  };

  const doCmd = (fn) => {
    const doc = getDoc();
    fn(doc);
    fireChange();
  };

  const toggleSource = () => {
    if (sourceMode) {
      setSourceMode(false);
      onChange?.(sourceValue);
    } else {
      const doc = getDoc();
      const html = dechipifyHtml(doc?.body?.innerHTML || "");
      setSourceValue(html);
      setSourceMode(true);
    }
  };

  const handleSourceChange = (e) => {
    const html = e.target.value;
    setSourceValue(html);
    skipNextSync.current = true;
    onChange?.(html);
  };

  // Insert raw Liquid source (single tag, output, or multi-token snippet)
  // and let chipifyHtml turn each {{...}}/{%...%} into a non-editable chip.
  const insertLiquid = (raw) => {
    setMergeOpen(false);
    setSnippetsOpen(false);
    if (sourceMode) {
      setSourceValue((prev) => {
        const next = prev + raw;
        skipNextSync.current = true;
        onChange?.(next);
        return next;
      });
      return;
    }
    const doc = getDoc();
    if (!doc) return;
    doc.body.focus();

    const wrapper = doc.createElement("span");
    wrapper.innerHTML = chipifyHtml(raw);

    const sel = doc.getSelection();
    if (!sel || sel.rangeCount === 0) {
      while (wrapper.firstChild) doc.body.appendChild(wrapper.firstChild);
      doc.body.appendChild(doc.createTextNode(" "));
      fireChange();
      return;
    }
    const range = sel.getRangeAt(0);
    range.deleteContents();
    const frag = doc.createDocumentFragment();
    while (wrapper.firstChild) frag.appendChild(wrapper.firstChild);
    const space = doc.createTextNode(" ");
    frag.appendChild(space);
    range.insertNode(frag);

    const newRange = doc.createRange();
    newRange.setStartAfter(space);
    newRange.collapse(true);
    sel.removeAllRanges();
    sel.addRange(newRange);
    fireChange();
  };

  const insertMergeTag = (tag) => insertLiquid(tag);
  const insertSnippet = (snippet) => insertLiquid(snippet.body);

  const insertLink = () => {
    const doc = getDoc();
    if (!doc) return;
    doc.body.focus();
    const sel = doc.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const startParent = range.startContainer.parentElement;
    const existing = startParent?.closest?.("a");
    if (existing) {
      setBubble({ type: "link", el: existing });
      return;
    }
    const text = sel.toString() || "link";
    const a = doc.createElement("a");
    a.href = "#";
    a.textContent = text;
    range.deleteContents();
    range.insertNode(a);
    const newRange = doc.createRange();
    newRange.selectNodeContents(a);
    sel.removeAllRanges();
    sel.addRange(newRange);
    fireChange();
    setBubble({ type: "link", el: a });
  };

  const insertImage = () => {
    const url = prompt("Enter image URL:");
    if (!url) return;
    const doc = getDoc();
    if (!doc) return;
    doc.body.focus();
    const sel = doc.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const img = doc.createElement("img");
    img.src = url;
    img.alt = "";
    range.insertNode(img);
    fireChange();
    setBubble({ type: "image", el: img });
  };

  const insertButton = () => {
    const doc = getDoc();
    if (!doc) return;
    doc.body.focus();
    const sel = doc.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const range = sel.getRangeAt(0);
    const wrap = doc.createElement("p");
    wrap.style.textAlign = "center";
    const a = doc.createElement("a");
    a.href = "#";
    a.textContent = "Click me";
    a.style.cssText = "display:inline-block;padding:12px 28px;background:#6366f1;color:#ffffff;text-decoration:none;border-radius:8px;font-weight:600;";
    wrap.appendChild(a);
    range.insertNode(wrap);
    fireChange();
    setBubble({ type: "button", el: a });
  };

  const insertTable = (rows, cols) => {
    const doc = getDoc();
    if (!doc) return;
    doc.body.focus();
    const sel = doc.getSelection();
    if (!sel || sel.rangeCount === 0) return;
    const table = doc.createElement("table");
    table.style.cssText = "border-collapse:collapse;width:100%;margin:0.5rem 0;";
    const tbody = doc.createElement("tbody");
    for (let r = 0; r < rows; r++) {
      const tr = doc.createElement("tr");
      for (let c = 0; c < cols; c++) {
        const td = doc.createElement("td");
        td.style.cssText = "border:1px solid #e2e8f0;padding:8px;";
        td.innerHTML = "&nbsp;";
        tr.appendChild(td);
      }
      tbody.appendChild(tr);
    }
    table.appendChild(tbody);
    sel.getRangeAt(0).insertNode(table);
    fireChange();
  };

  useEffect(() => {
    const handle = (e) => {
      if (mergeRef.current && !mergeRef.current.contains(e.target)) setMergeOpen(false);
      if (snippetsRef.current && !snippetsRef.current.contains(e.target)) setSnippetsOpen(false);
      if (foreColorRef.current && !foreColorRef.current.contains(e.target)) setForeOpen(false);
      if (backColorRef.current && !backColorRef.current.contains(e.target)) setBackOpen(false);
      if (tableGridRef.current && !tableGridRef.current.contains(e.target)) setTableGridOpen(false);
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  return (
    <div className="wysiwyg-editor">
      <div className="wysiwyg-toolbar">
        <div className="wysiwyg-toolbar-group">
          <button type="button" title="Undo" onClick={() => execCommand("undo")}><Undo2 size={15} /></button>
          <button type="button" title="Redo" onClick={() => execCommand("redo")}><Redo2 size={15} /></button>
        </div>

        <div className="wysiwyg-toolbar-group">
          <select
            className="wysiwyg-select"
            onChange={(e) => { if (e.target.value) { doCmd((d) => formatBlock(d, e.target.value)); e.target.value = ""; } }}
            defaultValue=""
            title="Paragraph style"
          >
            <option value="" disabled>Paragraph</option>
            <option value="P">Paragraph</option>
            <option value="H1">Heading 1</option>
            <option value="H2">Heading 2</option>
            <option value="H3">Heading 3</option>
            <option value="H4">Heading 4</option>
            <option value="BLOCKQUOTE">Quote</option>
          </select>
        </div>

        <div className="wysiwyg-toolbar-group">
          <select
            className="wysiwyg-select"
            onChange={(e) => { if (e.target.value) { doCmd((d) => applyFontSize(d, parseInt(e.target.value))); e.target.value = ""; } }}
            defaultValue=""
            title="Font size"
          >
            <option value="" disabled>Size</option>
            {[10, 12, 14, 16, 18, 20, 24, 32].map((s) => <option key={s} value={s}>{s}px</option>)}
          </select>
        </div>

        <div className="wysiwyg-toolbar-group">
          <button type="button" title="Bold" onClick={() => execCommand("bold")}><Bold size={15} /></button>
          <button type="button" title="Italic" onClick={() => execCommand("italic")}><Italic size={15} /></button>
          <button type="button" title="Underline" onClick={() => execCommand("underline")}><Underline size={15} /></button>
          <button type="button" title="Strikethrough" onClick={() => execCommand("strikeThrough")}><Strikethrough size={15} /></button>
        </div>

        <div className="wysiwyg-toolbar-group" style={{ position: "relative" }}>
          <div ref={foreColorRef} style={{ position: "relative", display: "inline-flex" }}>
            <button type="button" title="Text color" onClick={() => setForeOpen((v) => !v)}><Palette size={15} /></button>
            {foreOpen && (
              <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 1500 }}>
                <ColorPopover value="#0f172a" onChange={(c) => doCmd((d) => applyForeColor(d, c))} onClose={() => setForeOpen(false)} />
              </div>
            )}
          </div>
          <div ref={backColorRef} style={{ position: "relative", display: "inline-flex" }}>
            <button type="button" title="Highlight color" onClick={() => setBackOpen((v) => !v)}><Highlighter size={15} /></button>
            {backOpen && (
              <div style={{ position: "absolute", top: "100%", left: 0, zIndex: 1500 }}>
                <ColorPopover value="#fef08a" onChange={(c) => doCmd((d) => applyBackColor(d, c))} onClose={() => setBackOpen(false)} />
              </div>
            )}
          </div>
        </div>

        <div className="wysiwyg-toolbar-group">
          <button type="button" title="Insert Link" onClick={insertLink}><Link size={15} /></button>
          <button type="button" title="Insert Image" onClick={insertImage}><Image size={15} /></button>
          <button type="button" title="Insert Button" onClick={insertButton}><MousePointer2 size={15} /></button>
          <div style={{ position: "relative", display: "inline-flex" }} ref={tableGridRef}>
            <button type="button" title="Insert Table" onClick={() => setTableGridOpen((v) => !v)}><TableIcon size={15} /></button>
            {tableGridOpen && <TableGridPicker onPick={insertTable} onClose={() => setTableGridOpen(false)} />}
          </div>
          <button type="button" title="Horizontal divider" onClick={() => doCmd(insertDivider)}><Minus size={15} /></button>
          <button type="button" title="Inline code" onClick={() => doCmd(toggleInlineCode)}><Code2 size={15} /></button>
          <button type="button" title="Blockquote" onClick={() => doCmd((d) => formatBlock(d, "BLOCKQUOTE"))}><Quote size={15} /></button>
        </div>

        <div className="wysiwyg-toolbar-group">
          <button type="button" title="Ordered List" onClick={() => execCommand("insertOrderedList")}><ListOrdered size={15} /></button>
          <button type="button" title="Unordered List" onClick={() => execCommand("insertUnorderedList")}><List size={15} /></button>
        </div>

        <div className="wysiwyg-toolbar-group">
          <button type="button" title="Align Left" onClick={() => execCommand("justifyLeft")}><AlignLeft size={15} /></button>
          <button type="button" title="Align Center" onClick={() => execCommand("justifyCenter")}><AlignCenter size={15} /></button>
          <button type="button" title="Align Right" onClick={() => execCommand("justifyRight")}><AlignRight size={15} /></button>
        </div>

        <div className="wysiwyg-toolbar-group">
          <button type="button" title="Indent" onClick={() => execCommand("indent")}><Indent size={15} /></button>
          <button type="button" title="Outdent" onClick={() => execCommand("outdent")}><Outdent size={15} /></button>
        </div>

        <div className="wysiwyg-toolbar-group">
          <select
            className="wysiwyg-select"
            onChange={(e) => { if (e.target.value) { doCmd((d) => applyLineHeight(d, e.target.value)); e.target.value = ""; } }}
            defaultValue=""
            title="Line height"
          >
            <option value="" disabled>Line-h</option>
            {["1", "1.25", "1.5", "1.75", "2"].map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
          <button type="button" title="Clear formatting" onClick={() => doCmd(clearFormatting)}><Eraser size={15} /></button>
        </div>

        <div className="wysiwyg-toolbar-group">
          <div className="wysiwyg-merge-tags" ref={mergeRef}>
            <button type="button" className="wysiwyg-merge-tags-btn" onClick={() => setMergeOpen(!mergeOpen)}>
              Merge Tags <ChevronDown size={12} />
            </button>
            {mergeOpen && (
              <div className="wysiwyg-merge-tags-dropdown">
                {MERGE_TAGS.map((tag) => (
                  <button key={tag.value} type="button" onClick={() => insertMergeTag(tag.value)}>
                    {tag.label} — <span style={{ opacity: 0.6 }}>{tag.value}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            type="button"
            title="Liquid syntax reference"
            onClick={() => setCheatsheetOpen(true)}
          >
            <HelpCircle size={15} />
          </button>
          <div className="wysiwyg-merge-tags" ref={snippetsRef}>
            <button type="button" className="wysiwyg-merge-tags-btn" onClick={() => setSnippetsOpen(!snippetsOpen)}>
              Liquid <ChevronDown size={12} />
            </button>
            {snippetsOpen && (
              <div className="wysiwyg-merge-tags-dropdown wysiwyg-snippets-dropdown">
                {LIQUID_SNIPPETS.map((s) => (
                  <button key={s.id} type="button" onClick={() => insertSnippet(s)} title={s.description}>
                    <div className="snippet-label">{s.label}</div>
                    <div className="snippet-desc">{s.description}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="wysiwyg-toolbar-group" style={{ marginLeft: "auto" }}>
          {liquidPreview && (
            <button
              type="button"
              className={`source-toggle ${previewMode ? "active" : ""}`}
              title="Liquid Preview (renders with sample contact + sender)"
              onClick={() => setPreviewMode((v) => !v)}
              disabled={sourceMode}
            >
              <Eye size={14} style={{ marginRight: 4 }} />
              {previewMode ? "Edit" : "Preview"}
            </button>
          )}
          <button
            type="button"
            className={`source-toggle ${sourceMode ? "active" : ""}`}
            title="Toggle HTML Source"
            onClick={toggleSource}
            disabled={previewMode}
          >
            <Code size={14} style={{ marginRight: 4 }} />
            {sourceMode ? "Visual" : "HTML"}
          </button>
        </div>
      </div>

      {liquidError && (
        <div className="wysiwyg-liquid-error" role="alert">
          <strong>Liquid syntax:</strong> {liquidError.message}
          {liquidError.line ? <span className="liquid-error-line"> (line {liquidError.line})</span> : null}
        </div>
      )}

      {previewMode ? (
        <div className="wysiwyg-preview-wrap">
          {previewLoading && <div className="wysiwyg-preview-status">Rendering preview…</div>}
          {previewError && <div className="wysiwyg-preview-status wysiwyg-preview-status-error">Preview error: {previewError}</div>}
          <iframe
            className="wysiwyg-iframe"
            title="Liquid Preview"
            srcDoc={previewHtml}
          />
        </div>
      ) : sourceMode ? (
        <textarea
          className="wysiwyg-source"
          value={sourceValue}
          onChange={handleSourceChange}
          spellCheck={false}
        />
      ) : (
        <iframe
          ref={iframeRef}
          className="wysiwyg-iframe"
          title="Email Editor"
        />
      )}

      {bubble?.type === "link" && (
        <LinkBubble
          anchorEl={bubble.el}
          iframeEl={iframeRef.current}
          onClose={() => setBubble(null)}
          onChange={fireChange}
        />
      )}
      {bubble?.type === "image" && (
        <ImageBubble
          anchorEl={bubble.el}
          iframeEl={iframeRef.current}
          onClose={() => setBubble(null)}
          onChange={fireChange}
        />
      )}
      {bubble?.type === "button" && (
        <ButtonBubble
          anchorEl={bubble.el}
          iframeEl={iframeRef.current}
          onClose={() => setBubble(null)}
          onChange={fireChange}
        />
      )}
      {bubble?.type === "table" && (
        <TableBubble
          anchorEl={bubble.el}
          cell={bubble.cell}
          iframeEl={iframeRef.current}
          onClose={() => setBubble(null)}
          onChange={fireChange}
        />
      )}
      {bubble?.type === "block" && (
        <BlockBubble
          anchorEl={bubble.el}
          iframeEl={iframeRef.current}
          onClose={() => setBubble(null)}
          onChange={fireChange}
        />
      )}
      {bubble?.type === "divider" && (
        <DividerBubble
          anchorEl={bubble.el}
          iframeEl={iframeRef.current}
          onClose={() => setBubble(null)}
          onChange={fireChange}
        />
      )}
      {bubble?.type === "liquid" && (
        <LiquidChipBubble
          anchorEl={bubble.el}
          iframeEl={iframeRef.current}
          onClose={() => setBubble(null)}
          onChange={fireChange}
        />
      )}
      {cheatsheetOpen && <LiquidCheatsheet onClose={() => setCheatsheetOpen(false)} />}
    </div>
  );
}
